/**
 * Light, dark, or whatever the phone is set to.
 *
 * Roof Board is used on a roof. A white screen in direct sun is a mirror, and
 * the same white screen at six in the morning in the truck is blinding — so
 * there is a dark theme, and a switch, and the switch has three positions
 * rather than two: somebody who has set their phone to go dark at sunset should
 * not have to set the app as well.
 *
 * Three rules keep this from being fiddly:
 *
 *  1. **"System" is the default and is a real choice**, not the absence of one.
 *     Nothing is written to storage until somebody picks light or dark.
 *  2. **The stamp on `<html>` is only ever `light` or `dark`.** The stylesheet
 *     reads the system preference through `prefers-color-scheme` on its own, so
 *     an unstamped document still gets the right theme — see styles.css.
 *  3. **The customer's document is always light.** It is a contract; it is
 *     read, printed and signed by somebody who never chose a theme. The
 *     presented document sets its own `data-theme="light"`, which is why the
 *     rule below never applies to it.
 */

export const THEME_CHOICES = ["system", "light", "dark"] as const;

export type ThemeChoice = (typeof THEME_CHOICES)[number];

/** What actually gets painted, once the system preference is known. */
export type Theme = "light" | "dark";

/** The key the choice is remembered under. */
export const THEME_STORAGE_KEY = "roofboard.theme";

/** Anything unrecognised — an old value, a typo, `null` — means "system". */
export function readThemeChoice(
  stored: string | null | undefined,
): ThemeChoice {
  return THEME_CHOICES.includes(stored as ThemeChoice)
    ? (stored as ThemeChoice)
    : "system";
}

/** The theme to paint, given the choice and what the device prefers. */
export function resolveTheme(choice: ThemeChoice, prefersDark: boolean): Theme {
  if (choice === "light" || choice === "dark") return choice;
  return prefersDark ? "dark" : "light";
}

/**
 * The next choice when somebody taps the switch.
 *
 * It cycles rather than toggling, because a two-position switch cannot get you
 * back to "follow my phone" once you have left it. Light → dark → system.
 */
export function nextThemeChoice(choice: ThemeChoice): ThemeChoice {
  if (choice === "light") return "dark";
  if (choice === "dark") return "system";
  return "light";
}

/** What the switch says it will do, for the button's label and title. */
export const THEME_LABEL: Record<ThemeChoice, string> = {
  system: "Match my phone",
  light: "Light",
  dark: "Dark",
};
